import React, { useCallback, useEffect, useRef, useState } from "react";
import "./HeroVariants.css";

/* ---------- shared bits ---------- */

/* sound presets: slightly inharmonic partials = bell-ish tones */
const CHIME = [
  { f: 523.25, g: 0.14, d: 2.6 },
  { f: 1307, g: 0.05, d: 1.8 },
  { f: 2093, g: 0.025, d: 1.1 },
];
/* low fundamental + detuned pair (beating) = meditative singing bowl */
const BOWL = [
  { f: 196, g: 0.17, d: 5.5 },
  { f: 197.4, g: 0.11, d: 5.5 },
  { f: 494, g: 0.055, d: 4 },
  { f: 787, g: 0.028, d: 2.6 },
];
const PLUCK = [
  { f: 740, g: 0.055, d: 0.5 },
  { f: 1108, g: 0.03, d: 0.35 },
];
const TICK = [
  { f: 987, g: 0.045, d: 0.16 },
  { f: 1975, g: 0.018, d: 0.1 },
];

const useSound = () => {
  const ctxRef = useRef(null);
  return useCallback((partials) => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!ctxRef.current) ctxRef.current = new Ctx();
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    partials.forEach(({ f, g, d }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(g, now + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + d);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + d + 0.1);
    });
  }, []);
};

const BellIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3c.8 0 1.5.7 1.5 1.5v.6c2.6.7 4.5 3 4.5 5.9v3.5l1.5 2.5H4.5L6 14.5V11c0-2.9 1.9-5.2 4.5-5.9v-.6C10.5 3.7 11.2 3 12 3Z"
      fill="currentColor"
    />
    <path d="M10 19a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const BellSvg = ({ className }) => (
  <svg viewBox="0 0 120 130" className={className} aria-hidden="true">
    <path d="M60 6 q4 0 4 5 v6 h-8 v-6 q0 -5 4 -5 Z" fill="#4e463a" />
    <path
      d="M60 14 C 78 14 88 30 89 52 C 90 72 94 82 102 88 L 18 88 C 26 82 30 72 31 52 C 32 30 42 14 60 14 Z"
      fill="#544a3c"
    />
    <path
      d="M60 14 C 50 16 42 30 41 52 C 40 72 38 80 34 86 L 52 86 C 50 74 49 60 50 46 C 51 30 54 18 60 14 Z"
      fill="#6a5e4c"
      opacity="0.55"
    />
    <rect x="14" y="88" width="92" height="9" rx="4.5" fill="#3d362c" />
    <circle cx="60" cy="106" r="9" fill="#2f2922" />
  </svg>
);

const Waveform = ({ active, bars = 10 }) => (
  <span className={`hv-wave${active ? " hv-wave-on" : ""}`} aria-hidden="true">
    {Array.from({ length: bars }).map((_, i) => (
      <i key={i} style={{ animationDelay: `${i * 70}ms` }} />
    ))}
  </span>
);

const VariantSwitcher = () => {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  return (
    <nav className="hv-switch" aria-label="Hero variants">
      {["A", "B", "C", "D"].map((v) => (
        <a key={v} href={`/${v}`} className={path === `/${v}` ? "hv-switch-on" : ""}>
          {v}
        </a>
      ))}
    </nav>
  );
};

/* isometric cube cluster, used in B (interactive) and C (static) */
const FLOWER_TONES = ["#e8b04b", "#d9855f", "#e4d9a0", "#d4a7c6"];

const CubeCluster = ({ className, interactive = false, muted = false }) => {
  const DX = 44;
  const DY = 26;
  const H = 54;
  const play = useSound();
  const [bumps, setBumps] = useState({});
  const [blooms, setBlooms] = useState([]);
  const [ringKey, setRingKey] = useState(0);

  const cubes = [
    { cx: 200, cy: 84, top: "#5f7350", left: "#4c5e3f", right: "#3f5034", letterL: "Y", flowers: true, letterFill: "rgba(235,235,215,0.55)", fx: "bloom" },
    { cx: 114, cy: 130, top: "#c9a271", left: "#a97e54", right: "#8d6743", letterL: "O" },
    { cx: 286, cy: 130, top: "#b9c4a4", left: "#94a17c", right: "#7c8a64", letterR: "U" },
    { cx: 160, cy: 182, top: "#e3ddc4", left: "#cbc3a4", right: "#b0a888", letterL: "N" },
    { cx: 248, cy: 182, top: "#9fc0c4", left: "#7fa3ad", right: "#688b95", letterR: "G", ripples: true, fx: "bell" },
  ];

  const handleHit = (idx, cube) => {
    if (!interactive) return;
    setBumps((b) => ({ ...b, [idx]: (b[idx] || 0) + 1 }));
    if (cube.fx === "bell") {
      if (!muted) play(BOWL);
      setRingKey((k) => k + 1);
    } else if (cube.fx === "bloom") {
      if (!muted) play(PLUCK);
      const fresh = Array.from({ length: 6 }).map((_, i) => {
        const u = Math.random() - 0.5;
        const v = Math.random() - 0.5;
        return {
          id: `${Date.now()}-${i}`,
          x: cube.cx + (u - v) * DX * 0.72,
          y: cube.cy + (u + v) * DY * 0.72,
          delay: i * 80,
          r: 2.4 + Math.random() * 1.8,
          color: FLOWER_TONES[Math.floor(Math.random() * FLOWER_TONES.length)],
        };
      });
      setBlooms((prev) => [...prev.slice(-12), ...fresh]);
    } else if (!muted) {
      play(TICK);
    }
  };

  return (
    <svg viewBox="0 0 400 300" className={className} aria-hidden={!interactive}>
      <ellipse cx="200" cy="248" rx="165" ry="32" fill="rgba(40,50,30,0.12)" />
      {cubes.map((c, idx) => {
        const { cx, cy, top, left, right } = c;
        return (
          <g
            key={`${idx}-${bumps[idx] || 0}`}
            className={`${interactive ? "hv-cube-hit " : ""}${bumps[idx] ? "hv-cube-bounce" : ""}`}
            onClick={() => handleHit(idx, c)}
          >
            <polygon
              points={`${cx - DX},${cy + H} ${cx},${cy + DY + H} ${cx + DX},${cy + H} ${cx},${cy + DY + H + 6}`}
              fill="rgba(0,0,0,0.10)"
            />
            <polygon points={`${cx - DX},${cy} ${cx},${cy + DY} ${cx},${cy + DY + H} ${cx - DX},${cy + H}`} fill={left} />
            <polygon points={`${cx + DX},${cy} ${cx},${cy + DY} ${cx},${cy + DY + H} ${cx + DX},${cy + H}`} fill={right} />
            <polygon points={`${cx},${cy - DY} ${cx + DX},${cy} ${cx},${cy + DY} ${cx - DX},${cy}`} fill={top} />
            {c.flowers &&
              [
                [-16, -2],
                [2, -12],
                [16, 2],
                [-2, 8],
                [10, -6],
              ].map(([fx, fy], fi) => (
                <g key={`f${fi}`}>
                  <circle cx={cx + fx} cy={cy + fy} r="3.2" fill={["#e8b04b", "#d9855f", "#e4d9a0"][fi % 3]} />
                  <circle cx={cx + fx} cy={cy + fy} r="1.2" fill="#5f4a2e" />
                </g>
              ))}
            {c.ripples && (
              <g stroke="rgba(255,255,255,0.5)" fill="none" strokeWidth="1.4">
                <ellipse cx={cx} cy={cy} rx="20" ry="10" />
                <ellipse cx={cx} cy={cy} rx="10" ry="5" />
              </g>
            )}
            {c.fx === "bell" && ringKey > 0 && (
              <g key={`ring-${ringKey}`} className="hv-ring" stroke="rgba(255,255,255,0.75)" fill="none" strokeWidth="1.6">
                <ellipse cx={cx} cy={cy} rx="24" ry="12" />
                <ellipse cx={cx} cy={cy} rx="24" ry="12" />
              </g>
            )}
            {c.fx === "bloom" &&
              blooms.map((b) => (
                <g key={b.id} className="hv-bloom" style={{ animationDelay: `${b.delay}ms` }}>
                  <path d={`M ${b.x} ${b.y + 3} q 1.5 -6 0 -10`} stroke="#3c4a30" strokeWidth="1.1" fill="none" />
                  <circle cx={b.x} cy={b.y - 8} r={b.r} fill={b.color} />
                  <circle cx={b.x} cy={b.y - 8} r={b.r * 0.36} fill="#5f4a2e" />
                </g>
              ))}
            {c.letterL && (
              <g transform={`translate(${cx - DX} ${cy}) skewY(30.6)`}>
                <text x="22" y="38" textAnchor="middle" fontSize="26" fontWeight="700" fill={c.letterFill || "rgba(30,30,20,0.42)"} fontFamily="Georgia, serif">
                  {c.letterL}
                </text>
              </g>
            )}
            {c.letterR && (
              <g transform={`translate(${cx} ${cy + DY}) skewY(-30.6)`}>
                <text x="22" y="38" textAnchor="middle" fontSize="26" fontWeight="700" fill="rgba(20,25,20,0.38)" fontFamily="Georgia, serif">
                  {c.letterR}
                </text>
              </g>
            )}
          </g>
        );
      })}
      {/* grass tufts */}
      {Array.from({ length: 14 }).map((_, i) => {
        const x = 30 + ((i * 71) % 340);
        const y = 200 + ((i * 37) % 70);
        return (
          <g key={`t${i}`} stroke="#77855c" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.8">
            <path d={`M ${x} ${y} q -2 -7 -5 -10`} />
            <path d={`M ${x} ${y} q 0 -8 1 -12`} />
            <path d={`M ${x} ${y} q 3 -6 6 -9`} />
          </g>
        );
      })}
    </svg>
  );
};

/* =========================================================
   VERSION A — real desktop
   ========================================================= */

/* macOS Big Sur-style blue folder */
const MacFolder = ({ width = 58 }) => (
  <svg viewBox="0 0 80 64" width={width} aria-hidden="true">
    <path d="M5 13 q0 -6 6 -6 h17 l6 6 h35 q6 0 6 6 v6 H5 Z" fill="#3f9ae5" />
    <rect x="5" y="16" width="70" height="41" rx="6" fill="#5fb2f2" />
    <rect x="5" y="16" width="70" height="12" rx="6" fill="rgba(255,255,255,0.22)" />
  </svg>
);

/* menu bar status icons */
const WifiIcon = () => (
  <svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true">
    <path d="M8 10.6 L5.6 8.1 a3.5 3.5 0 0 1 4.8 0 Z" fill="currentColor" />
    <path d="M3.7 6.2 a6.4 6.4 0 0 1 8.6 0 M1.6 4 a9.4 9.4 0 0 1 12.8 0" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" />
  </svg>
);

const SearchIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" aria-hidden="true">
    <circle cx="6" cy="6" r="4.4" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M9.4 9.4 L12.6 12.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ControlCenterIcon = () => (
  <svg width="14" height="12" viewBox="0 0 15 13" aria-hidden="true">
    <rect x="0.7" y="0.7" width="13.6" height="5" rx="2.5" stroke="currentColor" strokeWidth="1.2" fill="none" />
    <circle cx="3.9" cy="3.2" r="1.7" fill="currentColor" />
    <rect x="0.7" y="7.3" width="13.6" height="5" rx="2.5" stroke="currentColor" strokeWidth="1.2" fill="none" />
    <circle cx="11.1" cy="9.8" r="1.7" fill="currentColor" />
  </svg>
);

const BatteryIcon = () => (
  <svg width="24" height="12" viewBox="0 0 25 12" aria-hidden="true">
    <rect x="0.7" y="0.9" width="20.5" height="10.2" rx="2.8" stroke="currentColor" strokeWidth="1.1" fill="none" opacity="0.65" />
    <rect x="2.2" y="2.4" width="14.5" height="7.2" rx="1.6" fill="currentColor" />
    <path d="M22.6 3.9 a2.3 2.3 0 0 1 0 4.2 Z" fill="currentColor" opacity="0.65" />
  </svg>
);

/* dock app icons */
const DockFinder = () => (
  <svg viewBox="0 0 56 56" aria-hidden="true">
    <rect width="56" height="56" rx="13" fill="url(#hvFinderR)" />
    <defs>
      <linearGradient id="hvFinderL" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#8edafc" />
        <stop offset="1" stopColor="#3ba4ef" />
      </linearGradient>
      <linearGradient id="hvFinderR" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#3d9df1" />
        <stop offset="1" stopColor="#1e6fd8" />
      </linearGradient>
      <clipPath id="hvFinderClip">
        <rect width="56" height="56" rx="13" />
      </clipPath>
    </defs>
    <path d="M0 0 h29 c-3 18 -3 38 0 56 H0 Z" fill="url(#hvFinderL)" clipPath="url(#hvFinderClip)" />
    <path d="M28.5 0 c-3 18 -3 38 0 56" stroke="rgba(20,60,110,0.5)" strokeWidth="1.4" fill="none" />
    <path d="M16 20 v6 M40 20 v6" stroke="#173e63" strokeWidth="2.6" strokeLinecap="round" />
    <path d="M14 36 q14 10 28 0" stroke="#173e63" strokeWidth="2.4" fill="none" strokeLinecap="round" />
  </svg>
);

const DockFolder = () => (
  <svg viewBox="0 0 56 56" aria-hidden="true">
    <path d="M6 16 q0 -5 5 -5 h13 l5 5 h22 q5 0 5 5 v4 H6 Z" fill="#3f9ae5" />
    <rect x="6" y="19" width="50" height="28" rx="5" fill="#5fb2f2" />
    <rect x="6" y="19" width="50" height="9" rx="4.5" fill="rgba(255,255,255,0.22)" />
  </svg>
);

const DockNotes = () => (
  <svg viewBox="0 0 56 56" aria-hidden="true">
    <rect width="56" height="56" rx="13" fill="#f7f6f2" />
    <path d="M0 0 h56 v14 H0 Z" fill="#f6c944" style={{ clipPath: "inset(0 round 13px 13px 0 0)" }} />
    <rect x="10" y="22" width="36" height="2.4" rx="1.2" fill="#c9c5b8" />
    <rect x="10" y="30" width="36" height="2.4" rx="1.2" fill="#c9c5b8" />
    <rect x="10" y="38" width="24" height="2.4" rx="1.2" fill="#c9c5b8" />
  </svg>
);

const DockGame = () => (
  <svg viewBox="0 0 56 56" aria-hidden="true">
    <rect width="56" height="56" rx="13" fill="url(#hvGameG)" />
    <defs>
      <linearGradient id="hvGameG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#8f6fe8" />
        <stop offset="1" stopColor="#6b3fd4" />
      </linearGradient>
    </defs>
    <path d="M17 22 h22 a8 8 0 0 1 8 8 l-1.5 7 a5 5 0 0 1 -9 2 l-2 -3 h-13 l-2 3 a5 5 0 0 1 -9 -2 l-1.5 -7 a8 8 0 0 1 8 -8 Z" fill="#f3f0fb" />
    <path d="M22 27 v8 M18 31 h8" stroke="#6b3fd4" strokeWidth="2.4" strokeLinecap="round" />
    <circle cx="36" cy="29" r="1.9" fill="#6b3fd4" />
    <circle cx="40.5" cy="33" r="1.9" fill="#6b3fd4" />
  </svg>
);

const DockMail = () => (
  <svg viewBox="0 0 56 56" aria-hidden="true">
    <rect width="56" height="56" rx="13" fill="url(#hvMailG)" />
    <defs>
      <linearGradient id="hvMailG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#5fb6f5" />
        <stop offset="1" stopColor="#1a70dd" />
      </linearGradient>
    </defs>
    <rect x="10" y="16" width="36" height="24" rx="4" fill="#fdfdfd" />
    <path d="M11 18 L28 31 L45 18" stroke="#c6cdd6" strokeWidth="1.8" fill="none" />
  </svg>
);

const DockMusic = () => (
  <svg viewBox="0 0 56 56" aria-hidden="true">
    <rect width="56" height="56" rx="13" fill="url(#hvMusicG)" />
    <defs>
      <linearGradient id="hvMusicG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#fc5f7a" />
        <stop offset="1" stopColor="#e63462" />
      </linearGradient>
    </defs>
    <path d="M24 40 a4.5 4.5 0 1 1 -3 -4.2 V20 l18 -4 v18 a4.5 4.5 0 1 1 -3 -4.2 V22 l-12 2.6 Z" fill="#fff" />
  </svg>
);

const DockTrash = () => (
  <svg viewBox="0 0 56 56" aria-hidden="true">
    <path d="M14 18 h28 l-2.5 30 a4 4 0 0 1 -4 3.6 h-15 a4 4 0 0 1 -4 -3.6 Z" fill="rgba(235,236,238,0.9)" stroke="#aeb2b8" strokeWidth="1.2" />
    <rect x="12" y="13" width="32" height="5" rx="2.5" fill="rgba(226,228,231,0.95)" stroke="#aeb2b8" strokeWidth="1" />
    <rect x="24" y="10" width="8" height="4" rx="2" fill="rgba(226,228,231,0.95)" stroke="#aeb2b8" strokeWidth="1" />
    <g stroke="#b8bcc2" strokeWidth="1.6" strokeLinecap="round">
      <path d="M21 23 l1 23 M28 23 v23 M35 23 l-1 23" />
    </g>
  </svg>
);

const SketchThumb = () => (
  <svg viewBox="0 0 64 78" width="52" height="64" aria-hidden="true">
    <rect x="2" y="2" width="60" height="74" rx="3" fill="#fbfaf5" stroke="rgba(0,0,0,0.14)" />
    <g stroke="#8a857a" strokeWidth="1.1" fill="none" strokeLinecap="round">
      <path d="M32 12 v10 M32 22 l-9 8 M32 22 l9 8 M23 30 v9 M41 30 v9" />
      <circle cx="32" cy="47" r="7" />
      <path d="M25 60 h14 M22 66 h20" strokeDasharray="2 3" />
    </g>
  </svg>
);

const GrassArt = () => (
  <svg viewBox="0 0 320 400" className="ha-grass" preserveAspectRatio="xMaxYMax meet" aria-hidden="true">
    <path d="M 236 400 q -12 -52 40 -58 q 44 -4 46 58 Z" fill="#8b8171" />
    <path d="M 236 400 q -8 -40 32 -48 q -20 16 -14 48 Z" fill="#7a7163" />
    {Array.from({ length: 22 }).map((_, i) => {
      const x = 30 + i * 13 + (i % 3) * 5;
      const h = 130 + ((i * 53) % 170);
      const bend = ((i % 5) - 2) * 16;
      const tone = ["#75835a", "#8b9769", "#5d6b48", "#9aa578"][i % 4];
      return (
        <g key={i}>
          <path
            d={`M ${x} 400 Q ${x + bend / 2} ${400 - h * 0.55} ${x + bend} ${400 - h}`}
            stroke={tone}
            strokeWidth={i % 4 === 0 ? 3 : 2}
            fill="none"
            strokeLinecap="round"
          />
          {i % 4 === 1 && <ellipse cx={x + bend} cy={400 - h - 4} rx="3" ry="7" fill={tone} transform={`rotate(${bend} ${x + bend} ${400 - h - 4})`} />}
        </g>
      );
    })}
    {[
      [70, 258],
      [150, 232],
      [117, 300],
    ].map(([x, y], i) => (
      <g key={`d${i}`}>
        {Array.from({ length: 6 }).map((_, p) => (
          <ellipse
            key={p}
            cx={x + 6 * Math.cos((p * Math.PI) / 3)}
            cy={y + 6 * Math.sin((p * Math.PI) / 3)}
            rx="3.4"
            ry="2.2"
            fill="#f5f2e6"
            transform={`rotate(${p * 60} ${x + 6 * Math.cos((p * Math.PI) / 3)} ${y + 6 * Math.sin((p * Math.PI) / 3)})`}
          />
        ))}
        <circle cx={x} cy={y} r="3" fill="#d9b95c" />
      </g>
    ))}
  </svg>
);

const DesktopIcon = ({ id, selected, onSelect, style, art, name, meta, onOpen }) => (
  <button
    type="button"
    className={`ha-icon${selected ? " ha-icon-selected" : ""}`}
    style={style}
    onClick={(e) => {
      e.stopPropagation();
      onSelect(id);
    }}
    onDoubleClick={onOpen}
  >
    <span className="ha-icon-art">{art}</span>
    <span className="ha-icon-name">{name}</span>
    <span className="ha-icon-meta">{meta}</span>
  </button>
);

export const HeroA = () => {
  const [selected, setSelected] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [playing, setPlaying] = useState(false);
  const [winState, setWinState] = useState("normal"); // normal | zoomed | hidden
  const [now, setNow] = useState(() => new Date());
  const play = useSound();

  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 15000);
    return () => window.clearInterval(t);
  }, []);

  const dateStr = now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).replace(",", "");
  const timeStr = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  const ring = () => {
    play(CHIME);
    setPlaying(true);
    window.setTimeout(() => setPlaying(false), 2200);
  };

  const startDrag = (e) => {
    if (e.target.closest(".ha-lights")) return;
    e.preventDefault();
    const ox = e.clientX - pos.x;
    const oy = e.clientY - pos.y;
    const move = (ev) => setPos({ x: ev.clientX - ox, y: ev.clientY - oy });
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const reopenWindow = () => {
    setWinState("normal");
    setPos({ x: 0, y: 0 });
  };

  return (
    <main className="ha-desktop" onClick={() => setSelected(null)}>
      <header className="ha-menubar">
        <div className="ha-menu-left">
          <span className="ha-apple"></span>
          <strong>Young He</strong>
          <a href="/">Work</a>
          <a href="/about">About</a>
          <a href="/world">Playground</a>
          <a href="/RandomLifeNotes">Notes</a>
          <a href="/feedback">Contact</a>
        </div>
        <div className="ha-menu-right">
          <span className="ha-status-icon"><BatteryIcon /></span>
          <span className="ha-status-icon"><WifiIcon /></span>
          <span className="ha-status-icon"><SearchIcon /></span>
          <span className="ha-status-icon"><ControlCenterIcon /></span>
          <span className="ha-clock">{dateStr}</span>
          <span className="ha-clock">{timeStr}</span>
        </div>
      </header>

      <div className="ha-wallpaper">
        <DesktopIcon
          id="folder-work"
          selected={selected === "folder-work"}
          onSelect={setSelected}
          style={{ right: "1.6%", top: "3%" }}
          art={<MacFolder />}
          name="Selected Work"
          meta="8 items"
        />
        <DesktopIcon
          id="folder-notes"
          selected={selected === "folder-notes"}
          onSelect={setSelected}
          style={{ right: "1.6%", top: "24%" }}
          art={<MacFolder />}
          name="Notes &amp; Ideas"
          meta="12 items"
        />
        <DesktopIcon
          id="sketch"
          selected={selected === "sketch"}
          onSelect={setSelected}
          style={{ right: "1.6%", top: "45%" }}
          art={<SketchThumb />}
          name="Sketch_01.svg"
          meta="24 KB"
        />
        <DesktopIcon
          id="chime"
          selected={selected === "chime"}
          onSelect={setSelected}
          onOpen={ring}
          style={{ right: "1.6%", top: "67%" }}
          art={
            <span className="ha-mp3">
              <BellSvg className="ha-mp3-bell" />
              <span className="ha-play" onClick={(e) => { e.stopPropagation(); ring(); }}>
                ▶
              </span>
            </span>
          }
          name="Chime.mp3"
          meta="1.2 MB"
        />

        <div className={`ha-window-wrap${winState === "hidden" ? " ha-window-hiding" : ""}`}>
          <div
            className={`ha-window${winState === "zoomed" ? " ha-window-zoomed" : ""}`}
            style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ha-titlebar" onPointerDown={startDrag}>
              <span className="ha-lights">
                <i className="ha-red" onClick={() => setWinState("hidden")}>
                  <b>×</b>
                </i>
                <i className="ha-yellow" onClick={() => setWinState("hidden")}>
                  <b>−</b>
                </i>
                <i className="ha-green" onClick={() => setWinState((s) => (s === "zoomed" ? "normal" : "zoomed"))}>
                  <b>⇱</b>
                </i>
              </span>
              <p>Quiet Desktop</p>
              <span className="ha-titlebar-icon">☰</span>
            </div>
            <div className="ha-window-body">
              <div className="ha-window-copy">
                <h1>
                  I design digital experiences that feel considered and&nbsp;calm.
                </h1>
                <p>
                  Product designer crafting thoughtful systems, interfaces, and
                  interactions for the long term.
                </p>
                <div className="ha-actions">
                  <a className="ha-btn" href="/">
                    Explore my work
                  </a>
                  <a className="ha-link" href="/about">
                    About me <span aria-hidden="true">→</span>
                  </a>
                </div>
              </div>
              <GrassArt />
            </div>
          </div>
        </div>

        <aside className="ha-sticky" aria-label="Sticky note">
          “Simplicity is not the lack of something. It's the presence of just
          enough.”
          <span>Nathan W. Morris</span>
        </aside>

        <div className={`ha-nowplaying${playing ? " ha-nowplaying-on" : ""}`} aria-hidden={!playing}>
          <BellIcon size={12} />
          <span>Chime.mp3</span>
          <Waveform active={playing} bars={7} />
        </div>

        <nav className="ha-dock" onClick={(e) => e.stopPropagation()}>
          <button type="button" data-label="Quiet Desktop" onClick={reopenWindow}>
            <DockFinder />
            {winState !== "hidden" && <i className="ha-dock-dot" />}
          </button>
          <a data-label="Selected Work" href="/">
            <DockFolder />
          </a>
          <a data-label="Notes &amp; Ideas" href="/RandomLifeNotes">
            <DockNotes />
          </a>
          <a data-label="Playground" href="/world">
            <DockGame />
          </a>
          <a data-label="Get in touch" href="/feedback">
            <DockMail />
          </a>
          <button type="button" data-label="Chime" onClick={ring}>
            <DockMusic />
            {playing && <i className="ha-dock-dot" />}
          </button>
          <span className="ha-dock-sep" />
          <button type="button" data-label="Trash">
            <DockTrash />
          </button>
        </nav>
      </div>
      <VariantSwitcher />
    </main>
  );
};

/* =========================================================
   VERSION B — Atomic Objects, UI right, bell ritual
   ========================================================= */

const CardDoodle = ({ kind }) => {
  const stroke = "currentColor";
  if (kind === "rocket")
    return (
      <svg viewBox="0 0 160 130" className="hb-doodle" aria-hidden="true">
        <path d="M80 14 l4 4 -4 4 -4 -4 Z" fill="none" stroke={stroke} />
        <path d="M80 26 v18" stroke={stroke} strokeDasharray="3 4" />
        <g stroke={stroke} fill="none">
          <path d="M80 52 q7 8 7 22 q0 14 -7 24 q-7 -10 -7 -24 q0 -14 7 -22 Z" />
          <path d="M73 82 l-8 10 8 -2 M87 82 l8 10 -8 -2" />
          <circle cx="80" cy="72" r="4" />
          <path d="M77 100 q3 8 6 0" />
        </g>
      </svg>
    );
  if (kind === "moon")
    return (
      <svg viewBox="0 0 160 130" className="hb-doodle" aria-hidden="true">
        <g stroke={stroke} fill="none">
          <circle cx="112" cy="40" r="17" />
          <circle cx="106" cy="35" r="3" />
          <circle cx="117" cy="46" r="2.2" />
          <path d="M42 70 l3 3 -3 3 -3 -3 Z M66 46 l3 3 -3 3 -3 -3 Z M55 96 l3 3 -3 3 -3 -3 Z" />
          <circle cx="34" cy="38" r="5" strokeDasharray="2 3" />
        </g>
      </svg>
    );
  if (kind === "path")
    return (
      <svg viewBox="0 0 160 130" className="hb-doodle" aria-hidden="true">
        <g stroke={stroke} fill="none">
          <path d="M20 100 q30 -10 44 -34 q14 -24 46 -30" strokeDasharray="3 5" />
          <path d="M20 100 l3 3 -3 3 -3 -3 Z M64 66 l3 3 -3 3 -3 -3 Z" />
          <path d="M114 32 q4 5 4 12 q0 7 -4 12 q-4 -5 -4 -12 q0 -7 4 -12 Z" />
          <path d="M110 50 l-5 6 5 -1 M118 50 l5 6 -5 -1" />
        </g>
      </svg>
    );
  return (
    <svg viewBox="0 0 160 130" className="hb-doodle" aria-hidden="true">
      <g stroke={stroke} fill="none">
        <rect x="52" y="30" width="56" height="56" rx="4" strokeDasharray="4 5" />
        <circle cx="80" cy="50" r="7" />
        <path d="M68 76 q12 -14 24 0" />
      </g>
    </svg>
  );
};

/* five tall boxes spelling Y-O-U-N-G; each lid opens to reveal a destination */
const NAME_BOXES = [
  { letter: "Y", label: "WORK", href: "/", cx: 230, cy: 100, top: "#dfe3cd", left: "#3f4f30", right: "#33402a", ink: "#2f3b24", well: "#22301b" },
  { letter: "O", label: "ABOUT", href: "/about", cx: 116, cy: 178, top: "#ecd3ab", left: "#c08540", right: "#a76d31", ink: "#7c4f1f", well: "#8a5a26" },
  { letter: "U", label: "EXPERIMENTS", href: "/world", cx: 344, cy: 178, top: "#d3dcbf", left: "#9cab7e", right: "#829267", ink: "#5c6b45", well: "#67754e" },
  { letter: "N", label: "NOTES", href: "/RandomLifeNotes", cx: 176, cy: 256, top: "#f0e9d2", left: "#dcd2ae", right: "#c4b892", ink: "#8d8156", well: "#a89b70" },
  { letter: "G", label: "CONTACT", href: "mailto:jiadiyounghe@gmail.com", cx: 290, cy: 256, top: "#d4dfe6", left: "#7ba0b9", right: "#5f87a2", ink: "#41637c", well: "#4a6b82" },
];

const NameBoxes = ({ className }) => {
  const A = 46;
  const B = 26;
  const H = 86;
  const play = useSound();
  const [openIdx, setOpenIdx] = useState(null);

  const toggle = (idx) => {
    setOpenIdx((cur) => {
      const next = cur === idx ? null : idx;
      play(next === null ? TICK : PLUCK);
      return next;
    });
  };

  return (
    <svg viewBox="0 0 460 420" className={className} role="group" aria-label="Y O U N G, tap a box to open it">
      <ellipse cx="230" cy="386" rx="180" ry="26" fill="rgba(40,50,30,0.1)" />
      {NAME_BOXES.map((b, idx) => {
        const { cx, cy } = b;
        const open = openIdx === idx;
        const chipY = cy - 74;
        const chipW = b.label.length * 8.4 + 44;
        return (
          <g key={b.letter} className="hb-box" onClick={() => toggle(idx)}>
            {/* body */}
            <polygon points={`${cx - A},${cy} ${cx},${cy + B} ${cx},${cy + B + H} ${cx - A},${cy + H}`} fill={b.left} />
            <polygon points={`${cx + A},${cy} ${cx},${cy + B} ${cx},${cy + B + H} ${cx + A},${cy + H}`} fill={b.right} />
            {/* dark interior, revealed when the lid swings open */}
            <polygon
              points={`${cx},${cy - B + 6} ${cx + A - 7},${cy} ${cx},${cy + B - 6} ${cx - A + 7},${cy}`}
              fill={b.well}
            />
            {/* lid with letter */}
            <g
              className="hb-lid"
              style={{
                transformOrigin: `${cx - A}px ${cy}px`,
                transform: open ? "translate(-18px, -30px) rotate(-24deg)" : "none",
              }}
            >
              <polygon points={`${cx},${cy - B} ${cx + A},${cy} ${cx},${cy + B} ${cx - A},${cy}`} fill={b.top} />
              <text
                x={cx}
                y={cy + 9}
                textAnchor="middle"
                fontSize="27"
                fontWeight="600"
                fill={b.ink}
                fontFamily="Georgia, serif"
              >
                {b.letter}
              </text>
            </g>
            {/* what's inside */}
            {open && (
              <g className="hb-box-pop">
                <a href={b.href} aria-label={b.label} onClick={(e) => e.stopPropagation()}>
                  <rect
                    x={cx - chipW / 2}
                    y={chipY - 15}
                    width={chipW}
                    height={30}
                    rx={15}
                    fill="#fbfaf3"
                    stroke="rgba(30,30,20,0.28)"
                  />
                  <text
                    x={cx}
                    y={chipY + 4}
                    textAnchor="middle"
                    fontFamily='"SF Mono", Menlo, monospace'
                    fontSize="11"
                    letterSpacing="1.6"
                    fill="#23221d"
                  >
                    {b.label} →
                  </text>
                </a>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export const HeroB = () => {
  const navLinks = [
    { label: "WORK", href: "/" },
    { label: "ABOUT", href: "/about" },
    { label: "EXPERIMENTS", href: "/world" },
    { label: "NOTES", href: "/RandomLifeNotes" },
    { label: "CONTACT", href: "mailto:jiadiyounghe@gmail.com" },
  ];

  return (
    <main className="hb-page">
      <header className="hb-nav">
        <span className="hb-nav-brand">YOUNG HE</span>
        <nav className="hb-nav-links">
          {navLinks.map((l) => (
            <a key={l.label} href={l.href}>
              {l.label}
            </a>
          ))}
        </nav>
      </header>

      <section className="hb-hero">
        <div className="hb-copy">
          <span className="hb-kicker">PRODUCT DESIGNER</span>
          <h1>
            Design, Fast and Slow.
          </h1>
          <h2 className="hb-headline">
          Think deep. Ship fast.
          </h2>
          <p className="hb-now">
            I am designing the #1 influencer marketing platform at{" "}
            <a href="/Collabstr">Collabstr</a>.
          </p>
          <div className="hb-chips">
            <span className="hb-chip">4+ YOE · 3+ yrs in B2B SaaS</span>
            <span className="hb-chip">A Designer Who Codes</span>
            <span className="hb-chip">WCAG 2.1 Auditor</span>
            <span className="hb-chip">M.A. Human Development Psychology, UBC</span>
          </div>
          <div className="hb-actions">
            <a className="hb-btn" href="/">
              View Selected Work <span aria-hidden="true">→</span>
            </a>
            <a className="hb-email" href="mailto:jiadiyounghe@gmail.com">
              Email Me
            </a>
          </div>
        </div>
        <div className="hb-stage">
          <NameBoxes className="hb-boxes" />
          <span className="hb-hint" aria-hidden="true">
            ◆ TAP THE OBJECTS
          </span>
        </div>
      </section>
      <VariantSwitcher />
    </main>
  );
};

/* =========================================================
   VERSION C — scattered atomic collage
   ========================================================= */

const RetroLights = () => (
  <span className="hc-lights">
    <i style={{ background: "#d96a5b" }} />
    <i style={{ background: "#dfb45c" }} />
    <i style={{ background: "#7d9a6a" }} />
  </span>
);

const SketchDoodle = () => (
  <svg viewBox="0 0 220 190" className="hc-sketch-art" aria-hidden="true">
    <path d="M110 10 v170" stroke="#9aa0a2" strokeDasharray="4 6" />
    <g stroke="#5d6163" fill="none" strokeWidth="1.2">
      {[
        [40, 34],
        [66, 58],
        [34, 80],
        [58, 104],
        [42, 130],
        [70, 148],
      ].map(([x, y], i) => (
        <path key={i} d={`M${x} ${y} l5 5 -5 5 -5 -5 Z`} />
      ))}
      <circle cx="176" cy="40" r="11" strokeDasharray="2 3" />
      <path d="M164 150 q5 -14 5 -24 q0 -10 -5 -18 q-5 8 -5 18 q0 10 5 24 Z" />
      <path d="M159 132 l-7 9 7 -2 M169 132 l7 9 -7 -2" />
      <path d="M164 156 q0 8 0 12" strokeDasharray="2 3" />
    </g>
  </svg>
);

const ObjectGlyph = ({ kind }) => {
  const s = "#5a5548";
  if (kind === "tree")
    return (
      <svg viewBox="0 0 40 40" aria-hidden="true">
        <path d="M20 6 l9 14 h-5 l7 10 H9 l7 -10 h-5 Z" fill="#7d8f69" />
        <rect x="18" y="30" width="4" height="6" fill={s} />
      </svg>
    );
  if (kind === "bell")
    return (
      <svg viewBox="0 0 40 40" aria-hidden="true">
        <path d="M20 6 c7 0 10 6 10 14 l3 8 H7 l3 -8 c0 -8 3 -14 10 -14 Z" fill="#544a3c" />
        <circle cx="20" cy="32" r="3" fill="#2f2922" />
      </svg>
    );
  if (kind === "cube")
    return (
      <svg viewBox="0 0 40 40" aria-hidden="true">
        <polygon points="20,6 33,13 20,20 7,13" fill="#a9b58f" />
        <polygon points="7,13 20,20 20,34 7,27" fill="#87936d" />
        <polygon points="33,13 20,20 20,34 33,27" fill="#6f7c58" />
      </svg>
    );
  if (kind === "house")
    return (
      <svg viewBox="0 0 40 40" aria-hidden="true">
        <path d="M20 8 l12 10 h-4 v14 H12 V18 H8 Z" fill="#b0a888" />
        <rect x="17" y="24" width="6" height="8" fill={s} />
      </svg>
    );
  return (
    <svg viewBox="0 0 40 40" aria-hidden="true">
      <rect x="10" y="10" width="20" height="20" rx="2" fill="#9fb2b6" />
      <rect x="10" y="10" width="20" height="7" rx="2" fill="#7fa3ad" />
    </svg>
  );
};

export const HeroC = () => {
  const [page, setPage] = useState(1);
  const [ringing, setRinging] = useState(false);
  const play = useSound();
  const ring = () => {
    play(CHIME);
    setRinging(true);
    window.setTimeout(() => setRinging(false), 1800);
  };

  return (
    <main className="hc-page">
      <svg className="hc-paths" viewBox="0 0 1440 800" preserveAspectRatio="none" aria-hidden="true">
        <g stroke="#b08a5a" fill="none" strokeWidth="1.4" strokeDasharray="1 7" strokeLinecap="round" opacity="0.65">
          <path d="M 320 250 C 400 180 480 170 560 210" />
          <path d="M 360 430 C 330 520 360 570 420 600" />
          <path d="M 1030 260 C 1090 300 1110 360 1090 420" />
          <path d="M 620 640 C 700 690 800 700 880 680" />
        </g>
        <g fill="#b08a5a" opacity="0.7">
          <circle cx="320" cy="250" r="4" />
          <circle cx="560" cy="210" r="4" />
          <circle cx="420" cy="600" r="4" />
          <circle cx="1090" cy="420" r="4" />
        </g>
        <g fill="#8f9a80" opacity="0.85">
          <path d="M 250 120 l7 11 h-4 l5 8 h-16 l5 -8 h-4 Z" />
          <path d="M 1180 560 l7 11 h-4 l5 8 h-16 l5 -8 h-4 Z" />
          <path d="M 90 620 l9 8 h-3 v9 h-12 v-9 h-3 Z" fill="#a39a82" />
        </g>
      </svg>

      <header className="hc-nav">
        <span className="hc-logo">YOUNG&nbsp;HE</span>
        <nav>
          <a href="/">WORK</a>
          <a href="/about">ABOUT</a>
          <a href="/RandomLifeNotes">ARCHIVE</a>
          <a href="/world">NOTES</a>
          <a href="/feedback">CONTACT</a>
        </nav>
      </header>

      <div className="hc-copy">
        <p className="hc-eyebrow">PRODUCT DESIGNER</p>
        <h1>
          I design systems that feel inevitable and <em>human.</em>
        </h1>
        <p className="hc-sub">
          Exploring order through play.
          <br />
          Building clarity, one interaction at a time.
        </p>
        <a className="hc-cta" href="/">
          VIEW SELECTED WORK <span aria-hidden="true">→</span>
        </a>
      </div>

      <div className="hc-window hc-case">
        <div className="hc-titlebar">
          <RetroLights />
          <span className="hc-title-text">CASE STUDY 04</span>
          <span className="hc-close">×</span>
        </div>
        <div className="hc-case-body">
          <div className="hc-case-copy">
            <h2>
              Design
              <br />
              System Docs
            </h2>
            <p>
              Designing clarity
              <br />
              in a complex product.
            </p>
            <div className="hc-tags">
              <span>SYSTEMS</span>
              <span>IA</span>
              <span>UX</span>
            </div>
            <p className="hc-year">2026</p>
          </div>
          <div className="hc-case-art">
            <CubeCluster className="hc-case-cubes" />
          </div>
        </div>
        <div className="hc-case-foot">
          <span>{page} OF 6</span>
          <span className="hc-pager">
            <button type="button" onClick={() => setPage((p) => ((p + 4) % 6) + 1)}>
              ‹
            </button>
            <button type="button" onClick={() => setPage((p) => (p % 6) + 1)}>
              ›
            </button>
          </span>
        </div>
      </div>

      <div className="hc-window hc-sketch">
        <div className="hc-titlebar">
          <RetroLights />
          <span className="hc-title-text">PLAYBOOK.SKETCH</span>
          <span className="hc-close">×</span>
        </div>
        <SketchDoodle />
      </div>

      <aside className="hc-window hc-note">
        <div className="hc-titlebar hc-titlebar-note">
          <span className="hc-title-text">FIELD NOTES</span>
          <span className="hc-close">×</span>
        </div>
        <p>
          Good systems feel
          <br />
          simple on the surface
          <br />
          because complexity
          <br />
          has been treated
          <br />
          with care.
        </p>
        <span className="hc-note-count">12 / 48</span>
      </aside>

      <div className="hc-window hc-tray">
        <div className="hc-titlebar">
          <RetroLights />
          <span className="hc-title-text">OBJECTS</span>
        </div>
        <div className="hc-tray-row">
          {["tree", "bell", "cube", "house", "block"].map((k) => (
            <span key={k} className="hc-object">
              <ObjectGlyph kind={k} />
            </span>
          ))}
        </div>
        <div className="hc-tray-dots">
          {Array.from({ length: 5 }).map((_, i) => (
            <i key={i} className={i === 0 ? "hc-dot-on" : ""} />
          ))}
        </div>
      </div>

      <button type="button" className={`hc-bell${ringing ? " hc-bell-ring" : ""}`} onClick={ring} aria-label="Ring bell">
        <BellSvg className="hc-bell-svg" />
      </button>

      <button type="button" className="hc-sound" onClick={ring}>
        <span aria-hidden="true">◁))</span> SOUND ON
      </button>
      <VariantSwitcher />
    </main>
  );
};

/* =========================================================
   VERSION D — macOS desktop (A) × Atomic Objects (B)
   ========================================================= */

export const HeroD = () => {
  const [selected, setSelected] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [playing, setPlaying] = useState(false);
  const [winState, setWinState] = useState("normal"); // normal | zoomed | hidden
  const [ritualOn, setRitualOn] = useState(true);
  const [ringing, setRinging] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const play = useSound();

  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 15000);
    return () => window.clearInterval(t);
  }, []);

  const dateStr = now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).replace(",", "");
  const timeStr = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  const ring = () => {
    if (!ritualOn) return;
    play(CHIME);
    setPlaying(true);
    window.setTimeout(() => setPlaying(false), 2200);
  };

  const ringCard = () => {
    if (!ritualOn) return;
    play(CHIME);
    setRinging(true);
    window.setTimeout(() => setRinging(false), 2000);
  };

  const cards = [
    { num: "01", title: "Work", kind: "rocket", href: "/" },
    { num: "02", title: "Notes", kind: "moon", href: "/RandomLifeNotes" },
    { num: "03", title: "Experiments", kind: "path", href: "/world" },
    { num: "04", title: "About", kind: "about", href: "/about" },
  ];

  const startDrag = (e) => {
    if (e.target.closest(".ha-lights")) return;
    e.preventDefault();
    const ox = e.clientX - pos.x;
    const oy = e.clientY - pos.y;
    const move = (ev) => setPos({ x: ev.clientX - ox, y: ev.clientY - oy });
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const reopenWindow = () => {
    setWinState("normal");
    setPos({ x: 0, y: 0 });
  };

  return (
    <main className="ha-desktop" onClick={() => setSelected(null)}>
      <header className="ha-menubar">
        <div className="ha-menu-left">
          <span className="ha-apple"></span>
          <strong>Young He</strong>
          <a href="/">Work</a>
          <a href="/about">About</a>
          <a href="/world">Playground</a>
          <a href="/RandomLifeNotes">Notes</a>
          <a href="/feedback">Contact</a>
        </div>
        <div className="ha-menu-right">
          <button
            type="button"
            className={`hd-ritual${ritualOn ? " hd-ritual-on" : ""}`}
            onClick={() => setRitualOn((v) => !v)}
            title={ritualOn ? "Sound ritual: on" : "Sound ritual: off"}
          >
            <BellIcon size={13} />
          </button>
          <span className="ha-status-icon"><BatteryIcon /></span>
          <span className="ha-status-icon"><WifiIcon /></span>
          <span className="ha-status-icon"><SearchIcon /></span>
          <span className="ha-status-icon"><ControlCenterIcon /></span>
          <span className="ha-clock">{dateStr}</span>
          <span className="ha-clock">{timeStr}</span>
        </div>
      </header>

      <div className="ha-wallpaper">
        <DesktopIcon
          id="folder-work"
          selected={selected === "folder-work"}
          onSelect={setSelected}
          style={{ right: "1.6%", top: "3%" }}
          art={<MacFolder />}
          name="Selected Work"
          meta="8 items"
        />
        <DesktopIcon
          id="folder-notes"
          selected={selected === "folder-notes"}
          onSelect={setSelected}
          style={{ right: "1.6%", top: "24%" }}
          art={<MacFolder />}
          name="Notes &amp; Ideas"
          meta="12 items"
        />
        <DesktopIcon
          id="sketch"
          selected={selected === "sketch"}
          onSelect={setSelected}
          style={{ right: "1.6%", top: "45%" }}
          art={<SketchThumb />}
          name="Sketch_01.svg"
          meta="24 KB"
        />
        <DesktopIcon
          id="chime"
          selected={selected === "chime"}
          onSelect={setSelected}
          onOpen={ring}
          style={{ right: "1.6%", top: "67%" }}
          art={
            <span className="ha-mp3">
              <BellSvg className="ha-mp3-bell" />
              <span className="ha-play" onClick={(e) => { e.stopPropagation(); ring(); }}>
                ▶
              </span>
            </span>
          }
          name="Chime.mp3"
          meta="1.2 MB"
        />

        <div className={`ha-window-wrap${winState === "hidden" ? " ha-window-hiding" : ""}`}>
          <div
            className={`ha-window hd-window${winState === "zoomed" ? " ha-window-zoomed" : ""}`}
            style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ha-titlebar" onPointerDown={startDrag}>
              <span className="ha-lights">
                <i className="ha-red" onClick={() => setWinState("hidden")}>
                  <b>×</b>
                </i>
                <i className="ha-yellow" onClick={() => setWinState("hidden")}>
                  <b>−</b>
                </i>
                <i className="ha-green" onClick={() => setWinState((s) => (s === "zoomed" ? "normal" : "zoomed"))}>
                  <b>⇱</b>
                </i>
              </span>
              <p>Atomic Objects</p>
              <span className="ha-titlebar-icon">☰</span>
            </div>
            <div className="hd-window-body">
              <div className="hd-hero-row">
                <div className="hd-copy">
                  <h1>
                    Atomic
                    <br />
                    Objects
                    <span className="hb-spark" aria-hidden="true">
                      ◇
                    </span>
                  </h1>
                  <p>
                    I design systems of simple things
                    <br />
                    that create clarity, connection, and calm.
                  </p>
                  <a className="hb-cta" href="/">
                    VIEW SELECTED WORK <span aria-hidden="true">→</span>
                  </a>
                </div>
                <div className="hd-visual">
                  <CubeCluster className="hd-cubes" interactive muted={!ritualOn} />
                  <span className="hb-visual-hint" aria-hidden="true">
                    ◇ TAP THE OBJECTS
                  </span>
                </div>
              </div>

              <div className="hb-cards hd-cards">
                {cards.map((c) => (
                  <a key={c.num} className="hb-card" href={c.href}>
                    <span className="hb-card-num">{c.num}</span>
                    <CardDoodle kind={c.kind} />
                    <span className="hb-card-foot">
                      <span className="hb-card-title">{c.title}</span>
                      <span aria-hidden="true">→</span>
                    </span>
                  </a>
                ))}
                <button type="button" className="hb-card hb-card-ritual" onClick={ringCard}>
                  <span className="hb-ritual-head">
                    <span className="hb-card-num">RITUAL</span>
                    <span className="hb-ritual-words">
                      Pause.
                      <br />
                      Listen.
                      <br />
                      Resonate.
                    </span>
                  </span>
                  <span className="hb-bell-stage">
                    <BellSvg className={`hb-bell${ringing ? " hb-bell-ring" : ""}`} />
                  </span>
                  <span className="hb-ritual-wavebox">
                    <Waveform active={ringing} bars={14} />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <aside className="ha-sticky" aria-label="Sticky note">
          “Simplicity is not the lack of something. It's the presence of just
          enough.”
          <span>Nathan W. Morris</span>
        </aside>

        <div className={`ha-nowplaying${playing ? " ha-nowplaying-on" : ""}`} aria-hidden={!playing}>
          <BellIcon size={12} />
          <span>Chime.mp3</span>
          <Waveform active={playing} bars={7} />
        </div>

        <nav className="ha-dock" onClick={(e) => e.stopPropagation()}>
          <button type="button" data-label="Atomic Objects" onClick={reopenWindow}>
            <DockFinder />
            {winState !== "hidden" && <i className="ha-dock-dot" />}
          </button>
          <a data-label="Selected Work" href="/">
            <DockFolder />
          </a>
          <a data-label="Notes &amp; Ideas" href="/RandomLifeNotes">
            <DockNotes />
          </a>
          <a data-label="Playground" href="/world">
            <DockGame />
          </a>
          <a data-label="Get in touch" href="/feedback">
            <DockMail />
          </a>
          <button type="button" data-label="Chime" onClick={ring}>
            <DockMusic />
            {playing && <i className="ha-dock-dot" />}
          </button>
          <span className="ha-dock-sep" />
          <button type="button" data-label="Trash">
            <DockTrash />
          </button>
        </nav>
      </div>
      <VariantSwitcher />
    </main>
  );
};
